export interface User {
  id: number;
  email: string;
  disabled: boolean;
  name: string;
  permissions: PermissionEnum[];
}

export interface IAuthToken {
  id: number;
  name: string;
  permissions: PermissionEnum[];
  marketplaceId?: number;
}

export enum PermissionEnum {
  // MANAGE_USERS = "MANAGE_USERS",
  MANAGE_SHOPS = "MANAGE_SHOPS",
  VIEW_LOGS = "VIEW_LOGS",
  VIEW_REPORTS = "VIEW_REPORTS",
  ADMIN = "ADMIN",
}

export interface AuthPayload {
  user: User;
  authToken: string;
}

export interface SchedulerValueInput {
  description: string;
  value: string;
}

export interface ConnectorBlackListsInput {
  shops: number[];
  invoices: number[];
}

export interface CreateMarketplaceConfig {
  useSubscription?: boolean;
  useChannels?: boolean;
  useBalancePlatforms?: boolean;
  useCommision?: boolean;
  miraklSsoEnabled?: boolean;
  onlyIdReferenceForBeneficiary?: boolean;
  marketplaceOperatorEmails: string[];
  manageBlockPaymentFlags: boolean;
  invoiceSyncScheduler?: SchedulerValueInput;
  accountFullSyncScheduler?: SchedulerValueInput;
  accountPartialSyncScheduler?: SchedulerValueInput;
  unsuspendNewShops: boolean;
  noOfSellerQuestions: number;
  maxInvoiceRetryCount: number;
  locale: string;
}

export interface MarketplaceConfig {
  account: AccountConfig;
  mirakl: MiraklConfig;
  blacklists: ConnectorBlackListsInput;
  // channels?: ChannelConfig[];
  [index: string]: any;
}

export interface InvoiceHistory {
  id: number;
  date: Date;
  status: string;
  reason: string;
  invoiceId: number;
}

export interface Invoice {
  marketplaceId: number;
  invoiceId: number;
  syncDate: Date;
  amountToShop: number;
  currency: string;
  amountToOperator: number;
  status: InvoiceStatus;
  shouldRetry: boolean;
  syncCycleId: number;
  shopId: number;
}

export interface AccountConfig extends CreateMarketplaceConfig {
  hopReturnUrl: string;
  [index: string]: any;
}

export interface ChannelConfig {
  id?: number;
  code: string;
  config: InternalChannelConfig;
  isDefault: boolean;
  balancePlatform?: string;
  lemApi: ApiConfig;
  balanceApi: ApiConfig;
  balanceAccountId: string;
  notificationSettings?: AdyenNotificationSettings;
  notificationUrl?: string;
  [index: string]: any;
}

export interface ChannelUpdateConfig {
  id?: number;
  code?: string;
  config?: InternalChannelConfig;
  isDefault?: boolean;
  balancePlatform?: string;
  lemApi?: ApiUpdateConfig;
  balanceApi?: ApiUpdateConfig;
  balanceAccountId?: string;
  preBalanceAccountId?: string;
  preBalancePlatformName?: string;
  notificationSettings?: AdyenNotificationUpdateSettings;
  notificationUrl?: string;
  [index: string]: any;
}

export interface ApiUpdateConfig {
  url?: string;
  isBasicAuth?: boolean;
  user?: string;
  password?: string;
  key?: string;
}

export interface AdyenNotificationUpdateSettings {
  configuration?: AdyenNotificationUpdateSecurity;
  transfer?: AdyenNotificationUpdateSecurity;
  transferInstrument?: AdyenNotificationUpdateSecurity;
  legalEntity?: AdyenNotificationUpdateSecurity;
}

export interface AdyenNotificationUpdateSecurity {
  user?: string;
  password?: string;
  hmac?: string;
}

export interface Channel extends ChannelConfig {
  marketplaceId: number;
}

export interface MiraklConfig {
  url: string;
  key: string;
  ssoClientId?: string;
  ssoClientSecret?: string;
  [index: string]: any;
}

export interface InternalChannelConfig{
  hopOptions?: HopOptions;
  routePriorities?: string;
  enablePreLiableCommissions: boolean;
}

export interface HopOptions{
  allowBankAccountFormatSelection: boolean;
  allowIntraRegionCrossBorderPayout: boolean;
  [index: string]: any;
}

export interface UpdatedValues {
  channels?: ChannelUpdateConfig;
  [index: string]: any;
}
export interface ApiConfig {
  url: string;
  isBasicAuth: boolean;
  user: string;
  password: string;
  key: string;
}

export interface AdyenNotificationSecurity {
  user: string;
  password: string;
  hmac: string;
}

export interface AdyenNotificationSettings {
  configuration?: AdyenNotificationSecurity;
  transfer?: AdyenNotificationSecurity;
  transferInstrument?: AdyenNotificationSecurity;
  legalEntity?: AdyenNotificationSecurity;
}

export enum InvoiceStatus {
  None = "None",
  Initiated = "Initiated",
  Succeeded = "Succeeded",
  Failed = "Failed",
}

export enum ShopSyncStatusEnum {
  OK = "OK",
  CreateFailed = "CreateFailed",
  UpdateFailed = "UpdateFailed",
  KYCFailed = "KYCFailed",
  KYCSucceeded = "KYCSucceeded",
  SyncBackOK = "SyncBackOK",
  SyncBackFailed = "SyncBackFailed",
  Missing = "Missing",
}

export enum ShopSyncEntityType {
  LegalEntity = "LegalEntity",
  AccountHolder = "AccountHolder",
  BalanceAccount = "BalanceAccount",
  TransferInstrument = "TransferInstrument",
}

export enum InvoiceOperationEnum {
  Payout = "Payout",
  PreliminaryChecks = "PreliminaryChecks",
  TransferCommission = "TransferCommission",
  TransferSubscription = "TransferSubscription",
  Pulled = "Pulled",
}
export interface HmacKey {
  key: string;
  markeplaceId: number;
}

export interface Marketplace {
  id: number;
  name: string;
  disabled: boolean;
  validated: boolean;
  validationFailReasons?: string;
  config: MarketplaceConfig;
  channels: Channel[];
  isInvoicePullInitialized: boolean;
  firstInvoiceCycleDate: Date;
  keys: HmacKey[];
}

export interface ShopHistory {
  id: number;
  date: Date;
  status: string;
  reason: string;
  marketplaceId: number;
  shopId: number;
}

export interface MarketplaceShop {
  marketplaceId: number;
  shopId: number;
  adyenAccountId: string;
  adyenAccountHolderId: string;
  lastSyncDate: Date;
  lastSyncBackDate: Date;
}

export interface ShopVerificationQuestion {
  key: string;
  title: string;
}

export interface HopValidationResponse {
  invalidResponseKeys: string[];
  redirectUrl: string;
}

export interface MarketplacePaginatedList {
  marketplaces: [Marketplace];
  totalCount: number;
}

export interface HopMiraklSsoQueryParameters {
  url: string;
  clientId: string;
  tenant: string;
  audience: string;
  state: string;
}

export interface ValidateHopMiraklSso {
  redirectUrl: string;
}

export interface UserPaginatedList {
  users: User[];
  totalCount: number;
}

export interface ShopVerificationQuestionResponse {
  key: string;
  response: string;
}

export interface GetListFilter {
  enabled: boolean;
  searchText: string;
  validated: boolean;
}

export interface PaginationInfo {
  size: number;
  index: number;
}

export interface CreateMarketplaceinterface {
  config: CreateMarketplaceConfig;
  name: string;
  disabled: boolean;
}

export interface UpdateMarketplaceConfig extends CreateMarketplaceConfig {
  adyenNotificationConfigurationId: number;
}

export interface UpdateMarketplace {
  config: UpdateMarketplaceConfig;
  name: string;
  disabled: boolean;
}

export interface CreateUserInput {
  email: string;
  disabled: boolean;
  name: string;
  password: string;
  permissions: PermissionEnum[];
  marketplaceId?: number;
}

export interface UpdateUserinterface {
  id: number;
  email: string;
  disabled: boolean;
  name: string;
  password: string;
  permissions: PermissionEnum[];
}
export interface RestartInvoicesInput {
  invoiceIds: number[];
  marketplaceId: number;
}

export interface PullAccountsInput {
  shopIds: number[];
  marketplaceId: number;
}

export interface ListOperatorPermissionsInput {
  marketplaceId: number;
  email: string;
}

export interface ListOperatorUsersOutput {
    status: string;
    missingRoles?: string[];
}

export interface ListOperatorUsersResponse {
    listOperatorUsers: ListOperatorUsersOutput;
}

export interface HopValidationInit {
  marketplaceName?: string;
  miraklSsoEnabled?: boolean;
}

export type Query = {
  login(password: string, email: string): AuthPayload;
  checkRecoverPasswordRequest(uuid: string): boolean;
  validateHopRequest(shopId: string, marketplaceId: string): HopValidationInit;
  getHopVerificationQuestions(
    shopId: string,
    marketplaceId: string
  ): [ShopVerificationQuestion];
  validateHopVerificationQuestions(
    responses: ShopVerificationQuestionResponse[],
    shopId: string,
    marketplaceId: string
  ): HopValidationResponse;
  hashId(number: number): string;
  getMarketplace(id: number): Marketplace;
  getMarketplaces(
    pagination: PaginationInfo,
    filter: GetListFilter
  ): MarketplacePaginatedList;
  test: string;
  getUser(userId: number): User;
  getUsers(
    pagination: PaginationInfo,
    filter: GetListFilter,
    markeplaceId?: number
  ): UserPaginatedList;
};

export type Mutation = {
  createPasswordRecoveryRequest(email: string): string;
  recoverPasswordAfterRequest(newPassword: string, uuid: string): AuthPayload;
  createMarketplace(data: CreateMarketplaceinterface): Marketplace;
  updateMarketplace(data: UpdateMarketplace): Marketplace;
  enableMarketplace(id: number): boolean;
  disableMarketplace(id: number): boolean;
  generateNotificationHmacKey(id: number): boolean;
  createUser(data: CreateUserInput): User;
  updateUser(data: UpdateUserinterface): User;
  changeUserPassword(userId: number, newPassword: string): boolean;
  deleteUser(userId: number): boolean;
  enableUser(userId: number): boolean;
  disableUser(userId: number): boolean;
};

export interface UserToBeEdited {
  userName: any;
  role: string;
  userEmail: string;
  password: string;
  confirmPassword: string;
}

export interface PermissionArrayItem {
  key: number;
  value: PermissionEnum;
  label: PermissionEnum;
}

export enum SettingTypes {
  "Account" = "Account",
  "Mirakl" = "Mirakl",
  "Channel" = "Channel",
  "BalancePlatform" = "BalancePlatform"
}

export enum ScheduledReportType {
  GetInvoicesStatusReport = 'GetInvoicesStatusReport',
  GetInvoicesLastMonthStatusReport = 'GetInvoicesLastMonthStatusReport',
  GetShopKycStatusReport = 'GetShopKycStatusReport',
}

export interface CreateReportType {
  id: number | undefined;
  sqlFunctionName: ScheduledReportType | undefined;
  marketplaceId?: number | undefined;
  name: string;
  hour: number | undefined;
  schedule: ReportSchedule | undefined;
  emails: string;
}

export enum ReportSchedule {
  Daily = 'Daily',
  EachMonday = 'EachMonday',
  EachTuesday = 'EachTuesday',
  EachWednesday = 'EachWednesday',
  EachThursday = 'EachThursday',
  EachFriday = 'EachFriday',
  EachSaturday = 'EachSaturday',
  EachSunday = 'EachSunday',
  FirstDayOfMonth = 'FirstDayOfMonth',
  LastDayOfMonth = 'LastDayOfMonth',
  OnDemand = 'OnDemand',
}

export type MarketplaceReports = {
  marketplaceId: string;
  name: string
}

export enum RoutePrioritiesEnum {
  regular = 'regular',
  fast = 'fast',
  wire = 'wire',
  instant = 'instant',
  crossBorder = 'crossBorder',
  internal = 'internal',
}

export interface RoutePrioritiesArrayItem {
  key: number;
  value: RoutePrioritiesEnum;
  label: RoutePrioritiesEnum;
}

export interface MarketplaceFilter {
  enabled?: boolean; 
  validated?: boolean; 
  searchText: string 
}